import type { Permission } from '@hbac/contract';
import { Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { Spinner } from '../components/ui/Spinner';
import { LandingPage } from '../public/LandingPage';
import { NewsListPage } from '../public/NewsListPage';
import { PublicLayout } from '../public/PublicLayout';
import { pages } from './lazy-pages';
import { paths, publicPaths } from './paths';
import { RequireMember, RequirePermission } from './RequirePermission';
import { RequireAuth } from './RequireAuth';
import { UnknownPath } from './UnknownPath';

const {
  AppLayout, Portal, CreateBill, BillsPage, MemberProfilePage, Approvals, SettingsTab,
  NotificationsPage, Logs, StatsPage, PostsAdmin, PostEditorPage, MailPage, CampaignPage, GenerationsPage, HandoverWizard,
  EventsPage, EventDetailPage, PresentPage, CheckInPage, TasksPage, DocsPage, DocPage, DocEditor,
  LoginScreen, NewsDetailPage, CompetitionPage, JoinPage,
} = pages;

const guard = (node: React.ReactNode) => (
  <ErrorBoundary>
    <Suspense fallback={<Loading />}>{node}</Suspense>
  </ErrorBoundary>
);
const needs = (permission: Permission, node: React.ReactNode) =>
  guard(<RequirePermission permission={permission}>{node}</RequirePermission>);

function Loading() {
  return <Spinner area="page" />;
}

/** Full-page stand-in while a layout chunk loads: the page colour, nothing
 *  that would jump when the real layout paints over it. */
function Blank() {
  return <div className="min-h-app bg-slate-50 dark:bg-navy-950" />;
}

export const router = createBrowserRouter([
  /**
   * Public site. No auth anywhere in this branch — a visitor reading the news
   * has no account, and must never be shown a login wall for doing so.
   */
  {
    path: '/',
    element: <PublicLayout />,
    children: [
      { index: true, element: guard(<LandingPage />) },
      { path: publicPaths.news.slice(1), element: guard(<NewsListPage />) },
      { path: publicPaths.newsPage.slice(1), element: guard(<NewsListPage />) },
      {
        path: publicPaths.newsDetail.slice(1),
        element: guard(<NewsDetailPage />),
      },
      { path: publicPaths.competition.slice(1), element: guard(<CompetitionPage />) },
      { path: publicPaths.join.slice(1), element: guard(<JoinPage />) },
    ],
  },

  // The login screen stands alone: it has its own full-page layout and belongs
  // to neither the public shell nor the member shell.
  { path: publicPaths.login.slice(1), element: <ErrorBoundary><Suspense fallback={<Blank />}><LoginScreen /></Suspense></ErrorBoundary> },

  /**
   * Member area. RequireAuth wraps the whole branch, so a signed-out visitor
   * is redirected once at the top rather than by each screen.
   */
  {
    path: '/',
    element: (
      <RequireAuth>
        <Suspense fallback={<Blank />}>
          <AppLayout />
        </Suspense>
      </RequireAuth>
    ),
    children: [
      { path: paths.portal.slice(1), element: guard(<Portal />) },
      { path: paths.bills.slice(1), element: guard(<BillsPage />) },
      { path: paths.create.slice(1), element: guard(<RequireMember><CreateBill /></RequireMember>) },
      // The directory is gone (Plan X2); its activity tab lives on as Thống kê.
      { path: paths.members.slice(1), element: <Navigate to={paths.dashboard} replace /> },
      { path: paths.memberProfile.slice(1), element: guard(<MemberProfilePage />) },
      { path: paths.approvals.slice(1), element: guard(<Approvals />) },
      { path: paths.settings.slice(1), element: guard(<SettingsTab />) },
      { path: paths.notifications.slice(1), element: guard(<NotificationsPage />) },
      { path: paths.tasks.slice(1), element: guard(<RequireMember><TasksPage /></RequireMember>) },
      { path: paths.docs.slice(1), element: guard(<RequireMember><DocsPage /></RequireMember>) },
      { path: paths.docNew.slice(1), element: needs('docs.manage', <RequireMember><DocEditor /></RequireMember>) },
      { path: paths.doc.slice(1), element: guard(<RequireMember><DocPage /></RequireMember>) },
      { path: paths.docEdit.slice(1), element: guard(<RequireMember><DocEditor /></RequireMember>) },
      {
        path: paths.dashboard.slice(1),
        element: guard(<StatsPage />),
      },
      {
        path: paths.posts.slice(1),
        element: needs('posts.manage', <PostsAdmin />),
      },
      { path: paths.postNew.slice(1), element: needs('posts.manage', <PostEditorPage />) },
      { path: paths.postEdit.slice(1), element: needs('posts.manage', <PostEditorPage />) },
      { path: paths.mail.slice(1), element: needs('mail.campaigns', <MailPage />) },
      { path: paths.campaign.slice(1), element: needs('mail.campaigns', <CampaignPage />) },
      { path: paths.logs.slice(1), element: needs('logs.view', <Logs />) },
      {
        path: paths.generations.slice(1),
        element: needs('generations.manage', <GenerationsPage />),
      },
      {
        path: paths.handover.slice(1),
        element: needs('generations.manage', <HandoverWizard />),
      },
      // Moved into Cài đặt (Plan X4): bookmarks and the old /noi-dung land on the tab.
      { path: paths.siteContent.slice(1), element: <Navigate to={paths.siteContentTab} replace /> },
      { path: paths.events.slice(1), element: needs('events.manage', <EventsPage />) },
      { path: paths.eventDetail.slice(1), element: needs('events.manage', <EventDetailPage />) },
      { path: paths.eventPresent.slice(1), element: needs('events.manage', <PresentPage />) },
      // Any signed-in member: the page a scanned QR opens.
      { path: paths.checkIn.slice(1), element: guard(<CheckInPage />) },
    ],
  },

  // Old Vietnamese paths reached by in-app navigation map to their new
  // names; anything else unknown goes to the landing page, not a dead end.
  { path: '*', element: <UnknownPath /> },
]);
